import { FormGroup } from '@angular/forms';

import { Options } from '@ph-core/enums/options.enum';
import { ConfigService } from '@ph-core/services/config/config.service';
import { CheckoutCustomerInfo } from '@ph-model/checkout/checkout-form-values.model';
import { Country } from '@ph-model/country-code';
import { UserResponse } from '@ph-model/login';
import { parseToNumber } from '@ph-shared/utils/parse-to-number';
import { ChargerForm, VehicleValues } from '@ph-shopping/store/vehicle/vehicle.store';

const environment = ConfigService.getEnvironment();
export interface SurchargeField {
  name: string;
  selected: boolean;
}

export class GetProductsRequest {
  pid?: string;
  hin: string | null = null;
  tin?: string;
  programName: string = '';
  vehicleCost: string = '';
  lienholderName: string;
  version: string = '';
  methodName: string = '';
  applicationName: string = '';
  languageCode: string;
  channel: string = '';
  vendorName: string = '';
  userName: string = '';
  password: string = '';

  constructor(
    public dealer: string,
    public vehicleType: string = '',
    public vin: string = '',
    public engineCC: string = '',
    public saleDate: string,
    public inServiceDate: string | null,
    public isAfterSale: string | null,
    public vehiclePurchaseDate: string | null,
    public vehicleCondition: string,
    public financeType: string | null,
    public financeAmount: number | string,
    public financeTerm: string = '',
    public vehicleYear: string,
    public vehicleMake: string,
    public vehicleModel: string,
    public vehicleTrim: string,
    public odometer: string,
    public msrp: string,
    public termMonth: string | null = '',
    public country: Country,
    public customerState: string | null = '',
    public dealerState: string = '',
    public defaultGetProduct: string = '',
    public surcharges: SurchargeField[] | null = [],
    public programId: string = '',
    public customerTaxExempt?: boolean | null,
    public negativeEquity?: boolean | null
  ) {
    this.lienholderName = '';
    const languageCode = localStorage.getItem('languageCode');
    this.languageCode = languageCode ? languageCode.replace('-', '_') : 'en_US';
  }

  static getDynamicVinNumber(form: FormGroup, unitTypeLabel: string): string | null {
    return form.get(unitTypeLabel)?.value || null;
  }

  static setGetProductsRequestByForm(form: FormGroup, user: UserResponse, unitTypeLabel: string): GetProductsRequest {
    // checking inservice date
    let inServiceDate = '';
    // as of 8/2 there is no functional date picker component
    // so had to add 'singleDate' after value to get formatted date value
    const inServiceDateControl = form.get('inServiceDate');
    if (inServiceDateControl && inServiceDateControl.value) {
      inServiceDate = inServiceDateControl.value.formatted;
    } else {
      inServiceDate = '';
    }

    const dynamicVin = GetProductsRequest.getDynamicVinNumber(form, unitTypeLabel);
    const stateProvince: string | null = environment.features.shared.supportProvince ? form.get('province')?.value : null;
    const country: Country = environment.countryCode;

    return new GetProductsRequest(
      user.cmsDealerNumber,
      undefined,
      dynamicVin ?? undefined,
      /* form.get('engineCC').value */ undefined,
      form.get('saleDate')?.value?.formatted || '',
      inServiceDate,
      form.get('isAfterSale')?.value || null,
      form.get('purchaseDate')?.value?.formatted || null,
      form.get('vehicleCondition')?.value || null,
      form.get('financeType')?.value || null,
      form.get('financeAmount')?.value || '',
      form.get('financeTerm')?.value ? String(form.get('financeTerm')?.value) : '',
      form.get('year')?.value || '',
      form.get('make')?.value || '',
      form.get('model')?.value || '',
      form.get('trim')?.value || '',
      form.get('odometer')?.value || '',
      form.get('msrp')?.value || '',
      undefined,
      country,
      stateProvince,
      user.dealerInfo.dealerState,
      user.defaultGetProduct,
      form.get('surcharges')?.value || null,
      user.programId,
      form.get('customerTaxExempt')?.value ? form.get('customerTaxExempt')?.value === Options.YES : null,
      form.get('negativeEquity')?.value ? form.get('negativeEquity')?.value === Options.YES : null
    );
  }

  static setGetProductsRequestByState(
    user: UserResponse,
    customer: CheckoutCustomerInfo,
    values: VehicleValues,
    chargerForm: ChargerForm
  ): GetProductsRequest {
    const { cmsDealerNumber, defaultGetProduct, programId, dealerInfo } = user;
    const { serialNumber, chargerPurchaseDate, chargerModel, chargerYear } = chargerForm;
    const {
      vin,
      hin,
      pid,
      tin,
      saleDate,
      inServiceDate,
      year,
      make,
      model,
      odometer,
      msrp,
      trim,
      isAfterSale,
      vehicleCondition,
      financeType,
      financeAmount,
      financeTerm,
      surcharges,
      customerTaxExempt,
      negativeEquity,
    } = values;
    const vinValue: string = vin || hin || pid || tin || serialNumber;
    const saleDateValue: string = saleDate?.formatted || chargerPurchaseDate?.formatted || '';
    const stateProvince: string | null = environment.features.shared.supportProvince
      ? customer.stateProvince || values.province || null
      : null;

    return new GetProductsRequest(
      cmsDealerNumber,
      undefined,
      vinValue,
      undefined,
      saleDateValue,
      inServiceDate?.formatted || null,
      isAfterSale || null,
      saleDate?.formatted || null,
      (vehicleCondition as string) || 'USED',
      (financeType as string) || null,
      parseToNumber(financeAmount),
      financeTerm ? String(financeTerm) : '',
      year || chargerYear || '',
      make || '',
      model || chargerModel?.toUpperCase() || '',
      trim || '',
      odometer || '0',
      msrp || '0',
      null,
      customer.country as Country,
      stateProvince,
      dealerInfo.dealerState,
      defaultGetProduct,
      (surcharges as SurchargeField[]) || null,
      programId,
      customerTaxExempt ? customerTaxExempt === Options.YES : null,
      negativeEquity ? negativeEquity === Options.YES : null
    );
  }
}
